import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import styles from './Form.module.css';
import { useGetFormByIdQuery, useSubmitFormMutation } from '../store/topicApi';
import Loader from '../helpers/loader';

const Form: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: topicData, error, isLoading } = useGetFormByIdQuery(id || '', { skip: !id });
  const [submitForm, { isLoading: isSubmitting }] = useSubmitFormMutation();

  const [answers, setAnswers] = useState<string[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(true);
  const [activeTab, setActiveTab] = useState<'tab1' | 'tab2'>('tab1');

  useEffect(() => {
    if (topicData) {
      const qLen = topicData?.questions?.length ?? 0;
      setAnswers(Array.from({ length: qLen }, (_, i) => answers[i] ?? ''));
      setHasSubmitted(topicData.submitted || false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicData]);

  const handleAnswerChange = (index: number, value: string) => {
    setAnswers(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleNext = () => {
    // Validate that questions 1 and 2 are filled
    if (!answers[0]?.trim()) {
      toast.error('Please answer Question 1 before proceeding.');
      return;
    }
    if (!answers[1]?.trim()) {
      toast.error('Please answer Question 2 before proceeding.');
      return;
    }
    setActiveTab('tab2');
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }

    // Validate that questions 1 and 2 are filled
    if (!answers[0]?.trim()) {
      toast.error('Please answer Question 1.');
      return;
    }
    if (!answers[1]?.trim()) {
      toast.error('Please answer Question 2.');
      return;
    }

    if (!id) {
      toast.error('Form ID is missing.');
      return;
    }
    try {
      await submitForm({ topicId: id, answers }).unwrap();
      toast.success('Form submitted successfully!');
      setHasSubmitted(true);
    } catch (err: any) {
      console.error('Error submitting form:', err);
      toast.error(err?.data?.message || 'Error submitting form');
    }
  };

  if (isLoading) return <Loader />;

  if (error) return <div>Error loading form.</div>;

  if (!topicData) return <div>No topic found.</div>;

  if (hasSubmitted) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', fontFamily: 'Arial, sans-serif' }}>
        <h1 style={{ color: '#333' }}>You have already submitted this form.</h1>
        <p style={{ color: '#555', marginTop: '10px' }}>Your response has been recorded.</p>
      </div>
    );
  }

  const questions: string[] = topicData?.questions ?? [];
  const tab1 = questions.slice(0, 2);         // Q1 & Q2
  const tab2 = questions.slice(2);            // Q3..end

  const renderQuestions = (qs: string[], startIndex: number, large?: boolean) => (
    qs.map((question, i) => {
      const absoluteIndex = startIndex + i;
      const isRequired = absoluteIndex === 0 || absoluteIndex === 1; // Q1 and Q2 are required
      return (
        <div key={absoluteIndex} style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
            {question}
            {isRequired && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
          </label>
          <textarea
            value={answers[absoluteIndex] ?? ''}
            onChange={(e) => handleAnswerChange(absoluteIndex, e.target.value)}
            required={isRequired}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '5px',
              border: '1px solid #ccc',
              minHeight: large ? 180 : 100
            }}
          />
        </div>
      );
    })
  );

  const handleTabClick = (tab: 'tab1' | 'tab2') => {
    // Don't validate when switching tabs, just switch
    setActiveTab(tab);
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ textAlign: 'center', color: '#333' }}>{topicData.title}</h1>
      <p style={{ textAlign: 'center', color: '#555' }}>Assigned to: {topicData.assignee}</p>

      {/* Tabs header - Using DIVs instead of buttons to avoid form interaction */}
      <div style={{
        display: 'flex',
        gap: '8px',
        borderBottom: '1px solid #e5e7eb',
        marginTop: '20px',
        marginBottom: '16px'
      }}>
        <div
          onClick={() => handleTabClick('tab1')}
          style={{
            padding: '10px 14px',
            borderBottom: activeTab === 'tab1' ? '3px solid #007bff' : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'tab1' ? '#007bff' : '#374151',
            fontWeight: 600,
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          Tab 1 (Q1–Q2)
        </div>
        <div
          onClick={() => handleTabClick('tab2')}
          style={{
            padding: '10px 14px',
            borderBottom: activeTab === 'tab2' ? '3px solid #007bff' : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'tab2' ? '#007bff' : '#374151',
            fontWeight: 600,
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          Tab 2 (Q3–Q{questions.length})
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ marginTop: '10px' }}>
        {/* Tab bodies */}
        {activeTab === 'tab1' && (
          <div>
            {renderQuestions(tab1, 0, true /* large textareas for tab1 */)}
          </div>
        )}
        {activeTab === 'tab2' && (
          <div>
            {renderQuestions(tab2, 2 /* start index for Q3 */, false)}
          </div>
        )}

        {/* Show Next button on Tab 1, Submit button on Tab 2 - Using DIVs to avoid form submission issues */}
        {activeTab === 'tab1' ? (
          <div
            onClick={handleNext}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              padding: '12px',
              backgroundColor: '#28a745',
              color: 'white',
              borderRadius: '6px',
              cursor: 'pointer',
              marginTop: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              userSelect: 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
          >
            Next →
          </div>
        ) : (
          <div
            onClick={isSubmitting ? undefined : handleSubmit}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              padding: '12px',
              backgroundColor: isSubmitting ? '#6c757d' : '#007bff',
              color: 'white',
              borderRadius: '6px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              marginTop: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              userSelect: 'none',
              opacity: isSubmitting ? 0.7 : 1,
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) e.currentTarget.style.backgroundColor = '#0056b3';
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) e.currentTarget.style.backgroundColor = '#007bff';
            }}
          >
            {isSubmitting && <span className={styles.loader} style={{ marginRight: 8 }}></span>}
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </div>
        )}
      </form>
    </div>
  );
};

export default Form;
